import React from "react";
import {useSelector} from "react-redux";
import {FormControlLabel, Grid, Radio, RadioGroup, Typography} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import {useFormikContext} from "formik";
import {Label} from "../../formInputs";
import {ErrorHelper, NumberTextField, SelectField, TextField, TextFieldCommon} from "../../formikFormInputs";
import {noop} from "../../../utils/commonUtils";
import {RootState} from "../../../reducers";
import {AccountType} from "../../../constants";


type GwtGen1RelationDetailsProps = { gen1User: any; setGen1User: Function; isGWTGen2?: boolean };

const otherTypeOfInsurance = [
    "Term life insurance",
    "Whole life insurance",
    "Universal life insurance",
    "Final expense insurance",
    "Simplified issue ",
    "Group life insurance",
];

enum YesNoVal {
    Yes = "1",
    No = "0"
}

const YesNoOptions = [
    { label: "Yes", value: YesNoVal.Yes },
    { label: "No", value: YesNoVal.No },
];


const GwtGen1PolicyDetails = ({ gen1User, setGen1User, isGWTGen2 }: GwtGen1RelationDetailsProps) => {
    const { errors, handleChange, setFieldValue, values } = useFormikContext<any>();

    const otherInsuranceTypesObj = otherTypeOfInsurance.map((name, key) => ({key: key+1, name}));


    const { user } = useSelector((state: RootState) => state.auth);

    const isAgent = user && user.role === AccountType.Agent;

    const fields = {
        policy: [
            {
                disabled: false,
                id: "currentKaiZenPolicy",
                isRadio: true,
                isRequired: true,
                name: "currentKaiZenPolicy",
                options: YesNoOptions,
            },
        ],
        yesFields: [
            {
                component: NumberTextField,
                disabled: !!(gen1User && gen1User.client && gen1User.client.trancheNo),
                id: "gen1TranceNo",
                label: <Label label={"Tranche #"} />,
                name: "gen1TranceNo",
                thousandSeparator:false,
                variant: "outlined",
            },
            {
                component: TextField,
                disabled: !!(gen1User && gen1User.client && gen1User.client.policyNumber),
                id: "gen1PolicyNumber",
                label: <Label label={"Policy Number"} />,
                name: "gen1PolicyNumber",
                variant: "outlined",
                thousandSeparator:false,
            },
            {
                component: TextField,
                disabled: !!(gen1User && gen1User.client && gen1User.client.policyNumber),
                label: <Label label={"Insurance Carrier Name"} required={!isAgent} />,
                name: "gen1ICN",
                variant: "outlined",
            },
            {
                id: "gen1DeathBAP",
                component: NumberTextField,
                disabled: !!(gen1User && gen1User.client && gen1User.client.policyNumber),
                InputProps: {
                    startAdornment: <InputAdornment className="margin0 dollerIcon" position="start">$</InputAdornment>,
                },
                label: <Label label={"Death Benefit Amount of the Policy"} required={!isAgent} />,
                name: "gen1DeathBAP",
                variant: "outlined",
            },
        ],
        noFieldHasInsurance: [
            {
                id: "hasCurrentInsuranceCoverage",
                label: "Is the Gen 1 Giftor over 55 and have current insurance coverage?",
                name: "hasCurrentInsuranceCoverage",
                options: YesNoOptions,
            },
        ],
        noFieldHasInsuranceYes: [
            {
                component: TextField,
                label: <Label label={"Insurance Carrier Name"} required={!isAgent} />,
                name: "otherCompanyName",
                variant: "outlined",
            },
            {
                component: SelectField,
                id: "otherType",
                label: <Label label={"Type of Insurance"} />,
                name: "otherType",
                onSelectChange: noop,
                options: otherInsuranceTypesObj,
                placeholder: "Type of Insurance",
            },
            {
                component: NumberTextField,
                id: "otherDeathBenefitAmount",
                InputProps: {
                    startAdornment: <InputAdornment className="margin0 dollerIcon" position="start">$</InputAdornment>,
                },
                label: <Label label={"Death Benefit Amount of the Policy"} required={!isAgent} />,
                name: "otherDeathBenefitAmount",
                variant: "outlined",
            },
            {
                component: TextField,
                id: "otherPolicyNumber",
                label: <Label label={"Policy Number"} />,
                name: "otherPolicyNumber",
                variant: "outlined",
                thousandSeparator:false,
            },
        ],
        noFieldHasInsuranceNo: [
            {
                id: "giftorInsurable",
                label: isAgent ? "Is the Giftor insurable?" : "Are you (the giftor) insurable?",
                name: "giftorInsurable",
                options: YesNoOptions,
            },
        ],
    };

    React.useEffect(() => {
        const resetFields = async () => {

            if (Number(values.currentKaiZenPolicy) === 0) {
                for (const field of fields.yesFields) {
                    if (field.name) {
                        await setFieldValue(field.name, undefined);
                    }
                }
            }

            if (Number(values.hasCurrentInsuranceCoverage) === 0 || Number(values.currentKaiZenPolicy) === 1) {
                for (const field of fields.noFieldHasInsuranceYes) {
                    if (field.name) {
                        await setFieldValue(field.name, undefined);
                    }
                }
            }

            if (Number(values.currentKaiZenPolicy) === 1) {
                await setFieldValue("hasCurrentInsuranceCoverage", undefined);
                await setFieldValue("giftorInsurable", undefined);
            }

            if (Number(values.hasCurrentInsuranceCoverage) === 1) {
                await setFieldValue("giftorInsurable", undefined);
            }
        };

        resetFields().then(() => {
        });
    }, [values && values.currentKaiZenPolicy, values && values.hasCurrentInsuranceCoverage]);

    const commonRadioProps = {
        className: "floatLeft radioGroup flexDirection ",
        color: "primary",
        onChange: handleChange,
    };
    const radioOptionLabel = (option: any, index: number) => (
            <FormControlLabel
                    className="radioGroup floatLeft w100px"
                    control={(
                            <Radio className="floatLeft" color="primary" />
                    )}
                    key={index}
                    label={option.label}
                    value={option.value}
            />
    );
    const clearExistingPolicyFields = () => {
        // clear has existing policy fields
        fields.yesFields.map(f => setFieldValue(f.name, ""));
    };
    const clearOtherPolicyFields = () => {
        // clear other policy fields
        fields.noFieldHasInsuranceYes.map(f => setFieldValue(f.name, ""));
        // clear has other policy radio selection
        fields.noFieldHasInsurance.map(f => setFieldValue(f.name, ""));
    };
    const onCurrentPolicyChange = async (e: any) => {
        if (e.target.value === YesNoVal.No) {
            setGen1User(null);
        }

        clearOtherPolicyFields();
        return handleChange(e);
    };
    const onHasInsuranceChange = (e: any) => {
        setFieldValue("giftorInsurable", "");
        clearExistingPolicyFields();
        return onCurrentPolicyChange(e);
    };

    return (
            <>
                {/*<link rel="stylesheet" href={`${process.env.PUBLIC_URL}/css/landingPageV2.css`} />*/}
                <Grid container spacing={3}>
                    <Grid style={{ display: "grid" }} item xs={12} md={6}>
                        <Typography variant="body2" className="floatLeft mt0 mb0 w100">
                            <Label
                                    label={<>{
                                        isAgent
                                                ? "Does the Giftor currently have a Kai-Zen policy?"
                                                : "Do you (the Giftor) currently have a Kai-Zen Policy?"}
                                    </>
                                    }
                                    required={!isGWTGen2}
                            />
                        </Typography>
                        {
                            fields.policy.map((field: any, index) => {
                                return (
                                        <React.Fragment key={index}>
                                            <RadioGroup
                                                    {...commonRadioProps}
                                                    name={field.name}
                                                    onChange={onCurrentPolicyChange}
                                                    value={values && values[field.name]}

                                            >
                                                {
                                                    field.options.map(radioOptionLabel)
                                                }
                                            </RadioGroup>
                                            <ErrorHelper errors={errors} name={field.name} />
                                        </React.Fragment>
                                );
                            })
                        }
                    </Grid>
                    {
                            values.currentKaiZenPolicy === YesNoVal.Yes &&
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    {
                                            values.currentKaiZenPolicy === YesNoVal.Yes &&
                                            fields.yesFields.map((field, index) => {
                                                return (
                                                        <Grid
                                                                item
                                                                key={index}
                                                                lg={6}
                                                                md={6}
                                                                sm={6}
                                                                xl={6}
                                                                xs={12}
                                                        >
                                                            <TextFieldCommon
                                                                    values={values}
                                                                    onChange={handleChange}
                                                                    {...field}
                                                            />
                                                            <ErrorHelper errors={errors} name={field.name} />
                                                        </Grid>
                                                );
                                            })
                                    }
                                </Grid>
                            </Grid>
                    }

                    {
                            values.currentKaiZenPolicy === YesNoVal.No &&
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    {
                                            values.currentKaiZenPolicy === YesNoVal.No &&
                                            fields.noFieldHasInsurance.map((field, index) => {
                                                return (
                                                        <Grid item key={index} xs={12}>
                                                            <Typography
                                                                    className="floatLeft mt0 mb0 w100"
                                                                    variant="body2"
                                                            >
                                                                <Label
                                                                        label={<>{
                                                                            isAgent
                                                                                    ? "Does the Giftor currently have insurance coverage?"
                                                                                    : "Do you (the Giftor) have current insurance coverage?"}
                                                                        </>
                                                                        }
                                                                        required={!isGWTGen2}
                                                                />
                                                            </Typography>

                                                            <RadioGroup
                                                                    {...commonRadioProps}
                                                                    {...field}
                                                                    onChange={onHasInsuranceChange}
                                                                    value={values && values[field.name]}
                                                            >
                                                                {
                                                                    field.options.map(radioOptionLabel)
                                                                }
                                                            </RadioGroup>
                                                            <ErrorHelper errors={errors} name={field.name} />
                                                        </Grid>
                                                );
                                            })
                                    }
                                </Grid>
                            </Grid>
                    }

                    {
                            values.currentKaiZenPolicy === YesNoVal.No &&
                            values.hasCurrentInsuranceCoverage === YesNoVal.Yes &&
                            fields.noFieldHasInsuranceYes.map((field, index) => {
                                return (
                                        <Grid item key={index} lg={6} md={4} sm={6} xl={6} xs={12}>
                                            <React.Fragment>
                                                <TextFieldCommon values={values} onChange={handleChange} {...field} />
                                                <ErrorHelper errors={errors} name={field.name} />
                                            </React.Fragment>
                                        </Grid>
                                );
                            })
                    }

                    {
                            values.currentKaiZenPolicy === YesNoVal.No &&
                            values.hasCurrentInsuranceCoverage === YesNoVal.No &&
                            fields.noFieldHasInsuranceNo.map((field, index) => {
                                return (
                                        <Grid item key={index} xs={12}>
                                            <Typography
                                                    className="floatLeft mt0 mb0 w100"
                                                    variant="body2"
                                            >
                                                <Label label={field.label} required={!isGWTGen2} />
                                            </Typography>

                                            <RadioGroup
                                                    {...commonRadioProps}
                                                    {...field}
                                                    value={values && values[field.name]}
                                            >
                                                {
                                                    field.options.map(radioOptionLabel)
                                                }
                                            </RadioGroup>
                                            <ErrorHelper errors={errors} name={field.name} />
                                        </Grid>
                                );
                            })
                    }
                </Grid>
            </>
    );
};

export default GwtGen1PolicyDetails;